import { useState, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import LanguageSwitchLink from "./LanguageSwitchLink";
import { getCookie, setCookie } from "cookies-next";
import { cookieOptions } from "@/utils/cookie";
import { getLanguagesApi } from "@/api/common";
import { useAppSelector } from "@/store/hooks";
import Icon from "@/components/Icon";

const Languages = ({ isMobile = false }: { isMobile?: boolean }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [locales, setLocales] = useState<string[]>(["sc", "tc", "en"]);
  const site = useAppSelector((state) => state.app.site);
  const [currentLocale, setCurrentLocale] = useState(router.locale || getCookie("NEXT_LOCALE") || "en");

  const handleSetLocales = async () => {
    const res: any = await getLanguagesApi({
      showCNLanguages: site === "cn" ? "y" : "n",
    });
    if (res && res.length > 0) {
      const arr: string[] = res.map((item: any) => item.language.toLowerCase());
      setLocales(arr);

      // 登录/免登流程进行中（在 /login 页，或 URL 仍带 appToken/token/udid/user）时，
      // 不要做语言归一化跳转：这一跳会清掉 appToken 等参数、打回 /login，
      // 从而打断 RedirectSignIn 的免登跳转。等登录落地到目标页后再归一化。
      const inLoginFlow =
        router.pathname === "/login" ||
        // _fromGeo：跨站落地，登录态经交接 cookie / # 带来，URL 上已没有 udid/user，语言交给 RedirectSignIn 登录后处理
        ["appToken", "token", "udid", "user", "_fromGeo"].some((key) => router.query[key]);
      if (inLoginFlow) return;

      // 页面渲染语言取决于 router.locale；无语言前缀时它是构建时烤死的 defaultLocale(en)，
      // 不会自动跟随 NEXT_LOCALE cookie。这里按「cookie 优先，无/非法则回退后端列表第一个」纠正它。
      // 手动切换也写 NEXT_LOCALE，故此逻辑天然尊重用户选择，无需额外标记。
      const cur = (router.locale || "en").toLowerCase();
      let target = (getCookie("NEXT_LOCALE") || "").toString().toLowerCase();
      if (!target || arr.indexOf(target) < 0) target = arr[0];

      if (target && target !== cur) {
        setCurrentLocale(target);
        setCookie("NEXT_LOCALE", target, { ...cookieOptions(), maxAge: 60 * 60 * 24 * 365 });
        // 保留页面展示参数（如 hideBack），仅剔除登录敏感参数，避免 App 内嵌态丢失
        // 取跳转这一刻的 router.query：渲染时解构的那份在静态页路由就绪前是空的，会把 hideBack 等参数丢掉
        const restQuery = { ...router.query };
        ["appToken", "token", "udid", "user"].forEach((key) => delete restQuery[key]);
        router.push({ pathname: router.pathname, query: restQuery }, undefined, { locale: target });
      }
    } else {
      if (site === "cn") {
        setLocales(["sc", "tc", "en"]);
      } else {
        setLocales(["en"]);
      }
    }
  };

  // 语言列表只由 site 决定（showCNLanguages 跟 site 走），与登录态无关，
  // 故不依赖 token；并加 site 空值守卫，避免 site 未就绪时先发一次多余请求。
  // 语言列表只由 site 决定（showCNLanguages 跟 site 走），与登录态无关，
  // 故不依赖 token；并加 site 空值守卫，避免 site 未就绪时先发一次多余请求。
  // 注意：App 免登流程的语言对齐不在这里做——那条流程用 history.replaceState 清 URL，
  // Next router 感知不到，此处 effect 不会触发；对齐逻辑放在 RedirectSignIn 落地处。
  // 还要等 router.isReady：静态页（如 /user/voucher）路由就绪前 router.query 是空的，
  // 上面 inLoginFlow 看不到 appToken / _fromGeo，会抢在免登前切语言、把 appToken 和 hideBack 一起丢掉
  useEffect(() => {
    if (!site || !router.isReady) return;
    handleSetLocales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, router.isReady]);

  useEffect(() => {
    setCurrentLocale(router.locale || getCookie("NEXT_LOCALE") || "en");
  }, [router.locale]);

  return (
    <div className="group relative flex h-full items-center ">
      <div className="flex h-full cursor-pointer items-center">
        <Icon name={isMobile ? "global-stone" : "global"} size={isMobile ? 16 : 20} />
        <div className="ml-2 text-sm text-stone-800 lg:text-base lg:text-white">{t(`menu.${currentLocale}`)}</div>
        {!isMobile && <Icon name={"arrow-down"} size={20} />}
      </div>
      <div className="absolute right-0 top-14 z-50 hidden flex-col bg-white	text-sm shadow shadow-pink-100 transition-all group-hover:flex lg:top-34px">
        {locales.map((locale) => (
          <LanguageSwitchLink key={locale} locale={locale} isDefault={currentLocale === locale} />
        ))}
      </div>
    </div>
  );
};

export default Languages;
